/**
 * What every part of the interface shares (part 11): the workspace, the translator, the platform and the interface's
 * own state (which panes and dialogs are open). The state is Solid signals, so the views follow it.
 */
import type { NoteAnchor } from '@cockatrice/storage';
import type { SplitOffer, Workspace } from '../shell/workspace.ts';
import type { Translate } from '../i18n/i18n.ts';
import type { Messages } from '../i18n/en.ts';
import type { Command, Platform } from '../shell/keys.ts';
import type { LayoutMode, NarrowPane } from '../shell/layout.ts';
import type { Route } from '../shell/route.ts';
import type { Shared } from '../shared/controller.ts';
import type { PreviewController } from '../preview/controller.ts';
import type { BundleController } from '../bundle/controller.ts';
import type { BackupController } from '../backup/controller.ts';
import type { EpubController } from '../epub/controller.ts';
import type { ReimportController } from '../reimport/controller.ts';
import type { Review as ReimportReview } from '../reimport/review.ts';
import type { DocxController } from '../docx/controller.ts';
import type { ExportController } from '../export/controller.ts';
import type { ImageInserter } from '../images/insert.ts';
import type { ImportResult } from '../images/importer.ts';
import type { ImportController, Review } from '../import/controller.ts';
import type { BundleImportController, BundleReview } from '../bundle-import/controller.ts';
import type { UpdateReview } from '../bundle-import/update.ts';
import type { FindController } from '../find/controller.ts';
import type { CountController } from '../count/controller.ts';
import type { PwaController } from '../pwa/controller.ts';
import type { HomeScreen } from '../pwa/home-screen.ts';
import type { InstallPrompt } from '../pwa/install-prompt.ts';
import type { Bitmap } from '@cockatrice/layout';
import type { Account } from '../account/account.ts';
import type { CredentialsContainer } from '../account/passkey.boundary.ts';
import type { AccountStep } from './account.ts';
import type { AccountTask } from './account-sheet.ts';
import type { LockTask } from './backup-lock.ts';
import type { OpenedBackup } from '../kdf/host.ts';
import type { Sharing } from '../share/sharing.ts';
import type { AccountManager } from '../account/manager.ts';
import { createSignal } from 'solid-js';
import type { Accessor, Setter } from 'solid-js';

/** A dialog the interface shows over the workspace (D1–D4). */
export type Dialog =
  | { readonly kind: 'delete'; readonly chapterId: string; readonly title: string }
  | { readonly kind: 'empty-trash'; readonly count: number }
  | { readonly kind: 'discard-copy'; readonly copyId: string }
  | { readonly kind: 'split'; readonly offer: SplitOffer; readonly title: string }
  /** Part 11b: the guide; `first` when it opened by itself (V1), so closing it puts the caret in the text (V11). */
  | { readonly kind: 'help'; readonly first: boolean }
  /** Part 18, V5: an imported book's review. */
  | { readonly kind: 'import'; readonly review: Review }
  /** Part 19b, table B: the word count and the goals. */
  | { readonly kind: 'words' }
  /** Part 22, Y2: the DOCX export's choice of look. */
  | { readonly kind: 'docx' }
  /** Part 23b, V′5: a re-imported Word file's review. */
  | { readonly kind: 'reimport'; readonly fileName: string; readonly review: ReimportReview; readonly pictures: number }
  /** Part 24c, Y3: a bundle's review. */
  | { readonly kind: 'bundle-import'; readonly review: BundleReview }
  /** Part 24d, Y′3: a staged update's review. */
  | { readonly kind: 'bundle-update'; readonly review: UpdateReview }
  /** Part 11h: the New book window (table BW). */
  | { readonly kind: 'new-book' }
  /**
   * Part 11d, H8; part 11h: the open book's details (table BW); `error` what stopped the New book window's photo, said as
   * the window opens on the book just made (BW7).
   */
  | { readonly kind: 'book'; readonly error?: string }
  /** Part 36c: an account action's sheet (tables PF, CH, FX, PD, DA). */
  | { readonly kind: 'account'; readonly task: AccountTask }
  /** Part 38d: Share a reading link of the open book. */
  | { readonly kind: 'share' }
  /** Part 38b: Share the open book with people; `person` opens that invite's settings (the Sharing page's Change). */
  | { readonly kind: 'invite'; readonly person: string | null }
  /** Part 41b, table E: a backup password's sheet: set, change, turn off (3.3.6) or check (E15–E17). */
  | { readonly kind: 'backup-lock'; readonly task: LockTask }
  /** Part 41b, E11–E13: a locked backup picked for import, asking for its password. */
  | { readonly kind: 'locked-bundle'; readonly file: File };

/** Part 19e, D′1: the Notes panel's tabs. */
export type NotesTab = 'chapter' | 'notebook';

/** Part 19e, D′2: which of This chapter's notes show: the open ones, the resolved ones, or all. */
export type NotesShow = 'open' | 'resolved' | 'all';

/** Part 19e, NO1, CP4: a passage chosen for the next note, in the chapter it was chosen in. */
export interface ChosenPassage {
  readonly chapterId: string;
  readonly anchor: NoteAnchor;
}

/** The interface's own state (table D, table Y). */
export class InterfaceState {
  /** Whether the chapter list is open (Y2, Y3). */
  readonly listOpen: Accessor<boolean>;
  readonly setListOpen: Setter<boolean>;
  /** The dialog shown, or null (D1). */
  readonly dialog: Accessor<Dialog | null>;
  /** CU15: whether a page's close-up is open: a window of the preview's own, which no shortcut runs under, as under a dialog. */
  readonly closeUpOpen: Accessor<boolean>;
  readonly setDialog: Setter<Dialog | null>;
  readonly setCloseUpOpen: Setter<boolean>;
  /** The command whose new key the settings are waiting for, or null (K9). */
  readonly capturing: Accessor<Command | null>;
  readonly setCapturing: Setter<Command | null>;
  /** The chapter whose title is being edited, or null. */
  readonly renaming: Accessor<string | null>;
  readonly setRenaming: Setter<string | null>;
  /** What a narrow screen shows: the text or the preview (Y8, Y9). */
  readonly narrowPane: Accessor<NarrowPane>;
  readonly setNarrowPane: Setter<NarrowPane>;
  /** Part 11d, table R: the page shown: home, the writer or the settings. Set through the shell's `go`, never alone. */
  readonly route: Accessor<Route>;
  readonly setRoute: Setter<Route>;
  /** Part 11e, SL1, SL2: the page the settings were opened from in this tab, or null when reached by an address. */
  readonly settingsFrom: Accessor<Route | null>;
  readonly setSettingsFrom: Setter<Route | null>;
  /** Part 19e, D′1: whether the Notes panel is open (a sheet on a narrow screen, D′10). */
  readonly notesOpen: Accessor<boolean>;
  readonly setNotesOpen: Setter<boolean>;
  /** Part 19e, D′1: the panel's tab. */
  readonly notesTab: Accessor<NotesTab>;
  readonly setNotesTab: Setter<NotesTab>;
  /** Part 19e, D′2: This chapter's Show. */
  readonly notesShow: Accessor<NotesShow>;
  readonly setNotesShow: Setter<NotesShow>;
  /** Part 19e, D′3: the Notebook's search. */
  readonly noteSearch: Accessor<string>;
  readonly setNoteSearch: Setter<string>;
  /** Part 19e, NO1, CP4: the passage the next note of This chapter goes on, or null for the whole chapter. */
  readonly passage: Accessor<ChosenPassage | null>;
  readonly setPassage: Setter<ChosenPassage | null>;
  /** Part 19e, Y′: whether text is chosen in the open chapter (the selection bar's Private note shows). */
  readonly textChosen: Accessor<boolean>;
  readonly setTextChosen: Setter<boolean>;
  /** Part 36a, table AC: the account gate's card asked for (a banner, settings, a link), or null for what the state gives. */
  readonly accountStep: Accessor<AccountStep | null>;
  readonly setAccountStep: Setter<AccountStep | null>;
  /** Part 36a: the settings section to open at (its list entry current, its heading focused), or null. */
  readonly settingsAt: Accessor<string | null>;
  readonly setSettingsAt: Setter<string | null>;
  /** Part 36a, SU6, C5: a verify link's token, taken from the address, until it is used or put away. */
  readonly verifyToken: Accessor<string | null>;
  readonly setVerifyToken: Setter<string | null>;
  /** Part 36c, LK: another mailed link's token (`''` for one that holds none), until it is used or put away. */
  readonly linkToken: Accessor<string | null>;
  readonly setLinkToken: Setter<string | null>;
  /** Part 38c: the invite whose book the read view shows (from the address `#/read/<invite>`), or null. */
  readonly reading: Accessor<string | null>;
  readonly setReading: Setter<string | null>;

  constructor(listOpen: boolean, route: Route) {
    [this.listOpen, this.setListOpen] = createSignal(listOpen);
    [this.dialog, this.setDialog] = createSignal<Dialog | null>(null);
    [this.closeUpOpen, this.setCloseUpOpen] = createSignal(false);
    [this.capturing, this.setCapturing] = createSignal<Command | null>(null);
    [this.renaming, this.setRenaming] = createSignal<string | null>(null);
    [this.narrowPane, this.setNarrowPane] = createSignal<NarrowPane>('text');
    [this.route, this.setRoute] = createSignal<Route>(route);
    [this.settingsFrom, this.setSettingsFrom] = createSignal<Route | null>(null);
    [this.notesOpen, this.setNotesOpen] = createSignal(false);
    [this.notesTab, this.setNotesTab] = createSignal<NotesTab>('chapter');
    [this.notesShow, this.setNotesShow] = createSignal<NotesShow>('open');
    [this.noteSearch, this.setNoteSearch] = createSignal('');
    [this.passage, this.setPassage] = createSignal<ChosenPassage | null>(null);
    [this.textChosen, this.setTextChosen] = createSignal(false);
    [this.accountStep, this.setAccountStep] = createSignal<AccountStep | null>(null);
    [this.verifyToken, this.setVerifyToken] = createSignal<string | null>(null);
    [this.linkToken, this.setLinkToken] = createSignal<string | null>(null);
    [this.settingsAt, this.setSettingsAt] = createSignal<string | null>(null);
    [this.reading, this.setReading] = createSignal<string | null>(null);
  }

  /** Part 11d, R3; part 11e, SL1: shows `route`, the settings remembering the page they were opened from. */
  enter(route: Route): void {
    if (route === 'settings') this.setSettingsFrom(this.route());
    this.setRoute(route);
  }
}

/** Everything a view needs. */
export interface Shell {
  readonly workspace: Workspace;
  /** Part 36a: the account on this device (off when the build names no relay). */
  readonly account: Account;
  /** Part 36a, LG5: the browser's credentials (`navigator.credentials`), for a passkey; undefined without. */
  readonly credentials: CredentialsContainer | undefined;
  /** Part 36c: the account page's lists and actions. */
  readonly manager: AccountManager;
  /** Part 38d: the reading links. */
  readonly sharing: Sharing;
  /** Part 38c: the books shared with the writer (invites accepted, their copies and notes). */
  readonly shared: Shared;
  /** Part 38c: shows the read view of claim `invite`, the address following it (`#/read/<invite>`) as a new history entry. */
  readonly read: (invite: string) => void;
  /** Part 36c, XP1, FX4: an object URL for a file the writer saves (its bytes and media type), and what lets it go. */
  readonly fileUrl: (bytes: Uint8Array<ArrayBuffer>, type: string) => string;
  readonly revokeUrl: (url: string) => void;
  /** Part 36c, FX4: copies text to the clipboard: true once it is there. */
  readonly copy: (text: string) => Promise<boolean>;
  /** Part 38b, IW1: the device's share sheet: true once shared; null where the browser has none. */
  readonly shareSheet: ((data: { readonly title: string; readonly text: string; readonly url: string }) => Promise<boolean>) | null;
  /** Part 36c, FX4: the browser's print dialog (the page's print style shows only what is marked for it). */
  readonly print: () => void;
  /** Part 41b, E11–E13: a locked backup opened with a backup password, in a KDF worker of its own. Never rejects. */
  readonly openBackup: (file: Blob, passphrase: string) => Promise<OpenedBackup>;
  readonly t: Translate<Messages>;
  /** The interface's language, for dates and language names. */
  readonly locale: string;
  readonly platform: Platform;
  /** The keys the editor binds (K4). */
  readonly reserved: ReadonlySet<string>;
  /** Wide or narrow, from the viewport (Y1). */
  readonly mode: Accessor<LayoutMode>;
  readonly ui: InterfaceState;
  /**
   * Part 11d, R3: shows `route`, the address following it as a new entry of the browser's history, so Back returns; the
   * page already showing does nothing.
   */
  readonly go: (route: Route) => void;
  /** Part 11e, SL2: the browser's Back, to the page before (the address then names it, R6). */
  readonly back: () => void;
  /** Part 11d, H4: the clock, in milliseconds, and the device's zone at a time, in minutes ahead of UTC. */
  readonly now: () => number;
  readonly zone: (at: number) => number;
  /** Reloads the page (the `failed` screen's "Try again"). */
  readonly reload: () => void;
  /** The service worker, whether the app works offline, and the update flow (part 40a, table U). */
  readonly pwa: PwaController;
  /** The browser's offer to install the app (part 40a, table P). */
  readonly installer: InstallPrompt;
  /** The Home Screen screen and banner on iOS (part 40a, table H). */
  readonly homeScreen: HomeScreen;
  /** The element chapter editors are mounted in; it stays in the page while the app runs. */
  readonly editorHost: HTMLElement;
  /** Moves the keyboard focus into the open chapter's text. */
  readonly focusEditor: () => void;
  /**
   * A press on the text pane's empty space: puts the caret at the place in the open chapter's text nearest the point
   * (viewport coordinates), extending the selection when `extend`, and focuses the text. False with no chapter open.
   */
  readonly placeCaret: (x: number, y: number, extend: boolean) => boolean;
  /** The live preview's requests to the layout worker (part 15). */
  readonly preview: PreviewController;
  /** The PDF export's requests to the same worker (part 21). */
  readonly exporter: ExportController;
  /** The Markdown bundle's worker and status (part 24a). */
  readonly bundler: BundleController;
  /** Backups outside the origin: the folder, the reminder and their notices (part 24e). */
  readonly backups: BackupController;
  /** The EPUB export's worker and status (part 20). */
  readonly epub: EpubController;
  /** The DOCX export's worker and status (part 22). */
  readonly docx: DocxController;
  /** Part 11h: a cover photo for book `bookId` through part 16a's pipeline at the cover's size (decision 37). */
  readonly addCover: (bookId: string, file: Blob) => Promise<ImportResult>;
  /** Images put in the text and what became of them (part 16b). */
  readonly images: ImageInserter;
  /** Imports of Word and Markdown files and their review (part 18). */
  readonly importer: ImportController;
  /** Re-imports of an edited DOCX export into the open book and their review (part 23b). */
  readonly reimporter: ReimportController;
  /** Bundle imports as a new book or a copy, and their review (part 24c). */
  readonly restorer: BundleImportController;
  /** The find panel and its worker (part 19a). */
  readonly finder: FindController;
  /** Word counts and goals, counted in the find worker (part 19b). */
  readonly counter: CountController;
  /** Puts a drawn page's pixels on a canvas, sized to them (the browser's bitmap renderer). */
  readonly present: (canvas: HTMLCanvasElement, bitmap: Bitmap) => void;
  /** Whether the page itself is visible (not a background tab, M1). */
  readonly pageVisible: Accessor<boolean>;
  /** Device pixels per CSS pixel (Z5). */
  readonly pixelRatio: () => number;
  /** Calls `changed` with the element's content width and height in CSS pixels now and whenever they change; returns what stops it. */
  readonly watchSize: (element: HTMLElement, changed: (width: number, height: number) => void) => () => void;
}

